import {
  AssetAccountType,
  AttorneyStage,
  DocumentTemplateType,
  FDItemType,
  InheritanceTrust,
  Prisma,
  type PrismaClient,
} from "@prisma/client-generated";
import {
  findAllFDCollections,
  type FDCollections,
} from "./find-all-collections.js";

import { hasAllRequiredIncomeYears } from "../../../utils/income-years.js";
import { allFDItemsHaveRequiredFields } from "../../../utils/fd-completeness.js";
import {
  clearDocumentApprovalsAndPDFsPlanIsNoOp,
  planClearDocumentApprovalsAndPDFs,
} from "./clear-document-approvals-and-pdfs-plan.js";
import { recomputeDocumentApprovalState } from "../../../documents/utils/document-approval-state.js";
import { isJointOwnershipAllowedForDocType } from "./joint-ownership.js";

type TxClient = Prisma.TransactionClient;
type ReadClient = TxClient | PrismaClient;

/**
 * Structural item shape consumed by `isFDApprovalComplete`; loose so it
 * matches both Prisma rows and the web client's JSON-serialized FD cache.
 */
interface ApprovalItem {
  partnerApprovedAt: Date | string | null;
  selfApprovedAt: Date | string | null;
  updatedAt?: Date | string | null | undefined;
  isJoint?: boolean | null | undefined;
  userId?: string | undefined;
}

/**
 * True when an approval predates the item's most recent content edit.
 * Missing `updatedAt` is treated as non-stale (legacy/cache shapes).
 */
function isApprovalStale(
  approvedAt: Date | string | null,
  updatedAt: Date | string | null | undefined,
): boolean {
  if (!approvedAt || !updatedAt) return false;
  const ms = (d: Date | string) =>
    d instanceof Date ? d.getTime() : new Date(d).getTime();
  return ms(updatedAt) > ms(approvedAt);
}

export function isSideFreshlyApproved(
  approvedAt: Date | string | null,
  updatedAt: Date | string | null | undefined,
): boolean {
  return !!approvedAt && !isApprovalStale(approvedAt, updatedAt);
}

/**
 * True when an FD item is approved for its current content: required
 * timestamp(s) are set and not stale against `updatedAt`.
 */
export function isFDApprovalComplete(
  item: ApprovalItem,
  options: { isJoint: boolean },
): boolean {
  const partnerOk = isSideFreshlyApproved(
    item.partnerApprovedAt,
    item.updatedAt,
  );
  if (!options.isJoint) return partnerOk;
  return (
    partnerOk && isSideFreshlyApproved(item.selfApprovedAt, item.updatedAt)
  );
}

/**
 * Clears document-level artifacts invalidated by an FD edit (or any other
 * PDF-input change). Active OR completed notarizations preserve all state
 * (Proof needs `proofFileId` stable during signing; completed is PoNR);
 * bubble imports preserve only cached PDFs and `reviewedAt` because those
 * files cannot be regenerated.
 *
 * Self-gating: callers may invoke this unconditionally. Cancel sites that
 * want the cleanup to actually run must soft-delete the notarization row
 * *before* calling this in the same tx.
 */
export async function clearDocumentApprovalsAndPDFs(params: {
  tx: TxClient;
  documentId: string;
  /**
   * Whoever's edit triggered the invalidation. Null is reserved for
   * system-originated cleanups with no authenticated user (currently the
   * Proof `transaction.expired` / `transaction.deleted` webhook).
   */
  updatedById: string | null;
}) {
  const { tx, documentId, updatedById } = params;
  const document = await tx.document.findUnique({
    where: { id: documentId, deletedAt: null },
    select: {
      attorneyAId: true,
      attorneyBId: true,
      attorneyAApprovedAt: true,
      attorneyBApprovedAt: true,
      attorneyConsultations: {
        where: {
          deletedAt: null,
          isApprovalRequired: true,
          attorney: {
            stage: AttorneyStage.APPROVED,
            deletedAt: null,
          },
        },
        select: { id: true, attorneyApprovedAt: true },
      },
      finalizedAt: true,
      finalizedAtPrior: true,
      reviewedAt: true,
      status: true,
      proofFileId: true,
      generatedPdfFileId: true,
      lastGeneratedInputHash: true,
    },
  });
  if (!document) return;

  const approvedRequiredConsultations = document.attorneyConsultations.filter(
    (consultation) => consultation.attorneyApprovedAt !== null,
  );

  if (
    ![
      document.attorneyAApprovedAt,
      document.attorneyBApprovedAt,
      approvedRequiredConsultations.length > 0,
      document.reviewedAt,
      document.proofFileId,
      document.generatedPdfFileId,
    ].some(Boolean)
  )
    return;

  const activeOrCompletedNotarization = await tx.notarization.findFirst({
    where: { documentId, deletedAt: null },
    select: { id: true },
  });

  const plan = planClearDocumentApprovalsAndPDFs({
    attorneyAApprovedAt: document.attorneyAApprovedAt,
    attorneyBApprovedAt: document.attorneyBApprovedAt,
    finalizedAt: document.finalizedAt,
    reviewedAt: document.reviewedAt,
    status: document.status,
    proofFileId: document.proofFileId,
    generatedPdfFileId: document.generatedPdfFileId,
    lastGeneratedInputHash: document.lastGeneratedInputHash,
    hasActiveOrCompletedNotarization: !!activeOrCompletedNotarization,
  });

  const hasConsultationApprovalsToClear =
    !plan.isPastPointOfNoReturn && approvedRequiredConsultations.length > 0;

  if (
    clearDocumentApprovalsAndPDFsPlanIsNoOp(plan) &&
    !hasConsultationApprovalsToClear
  )
    return;

  const {
    hasAttorneyApprovalsToClear,
    hasCachedPdfsToClear,
    hasReviewedAtToClear,
  } = plan;

  // Soft-delete the file rows before nulling the IDs, mirroring the
  // `/documents/:id/generate` cleanup so file metadata is preserved.
  if (hasCachedPdfsToClear) {
    const deletedAt = new Date();
    if (document.generatedPdfFileId) {
      await tx.file.update({
        where: { id: document.generatedPdfFileId },
        data: { deletedAt, updatedById },
      });
    }
    if (document.proofFileId) {
      await tx.file.update({
        where: { id: document.proofFileId },
        data: { deletedAt, updatedById },
      });
    }
  }

  const data: Prisma.DocumentUncheckedUpdateInput = updatedById
    ? { updatedById }
    : {};
  if (hasConsultationApprovalsToClear) {
    await Promise.all(
      approvedRequiredConsultations.map((consultation) =>
        tx.attorneyConsultation.updateMany({
          where: {
            id: consultation.id,
            documentId,
            deletedAt: null,
            isApprovalRequired: true,
            attorneyApprovedAt: consultation.attorneyApprovedAt,
            attorney: {
              stage: AttorneyStage.APPROVED,
              deletedAt: null,
            },
          },
          data:
            updatedById === null
              ? {
                  attorneyApprovedAt: null,
                  attorneyApprovedAtPrior: consultation.attorneyApprovedAt,
                }
              : {
                  attorneyApprovedAt: null,
                  attorneyApprovedAtPrior: consultation.attorneyApprovedAt,
                  updatedById,
                },
        }),
      ),
    );
  }
  if (hasAttorneyApprovalsToClear || hasConsultationApprovalsToClear) {
    const recomputed = recomputeDocumentApprovalState(document, {
      attorneyAId: document.attorneyAId,
      attorneyBId: document.attorneyBId,
      attorneyAApprovedAt: hasAttorneyApprovalsToClear
        ? null
        : document.attorneyAApprovedAt,
      attorneyBApprovedAt: hasAttorneyApprovalsToClear
        ? null
        : document.attorneyBApprovedAt,
      requiredConsultationApprovals: document.attorneyConsultations.map(() => ({
        attorneyApprovedAt: null,
      })),
    });
    if (hasAttorneyApprovalsToClear) {
      data.attorneyAApprovedAt = null;
      data.attorneyBApprovedAt = null;
      data.attorneyAApprovedAtPrior = document.attorneyAApprovedAt;
      data.attorneyBApprovedAtPrior = document.attorneyBApprovedAt;
    }
    data.status = recomputed.status;
    data.statusModifiedAt = recomputed.statusModifiedAt;
    data.finalizedAt = recomputed.finalizedAt;
    data.finalizedAtPrior = recomputed.finalizedAtPrior;
  }
  if (hasCachedPdfsToClear) {
    data.generatedPdfFileId = null;
    data.proofFileId = null;
  }
  if (hasReviewedAtToClear) data.reviewedAt = null;
  await tx.document.update({ where: { id: documentId }, data });
}

/**
 * True when an FD edit should clear per-item approval timestamps; only
 * pre-generation. Post-generation the staleness gate handles invalidation.
 */
export async function shouldClearFDApprovalsOnFDChange(
  tx: ReadClient,
  documentId: string,
): Promise<boolean> {
  const doc = await tx.document.findUnique({
    where: { id: documentId },
    select: { lastGeneratedAt: true },
  });
  return !doc?.lastGeneratedAt;
}

export async function clearFDApprovals(params: {
  tx: TxClient;
  fdItemType: FDItemType;
  fdItemId: string;
  documentId: string;
  updatedById: string;
}) {
  const { tx, fdItemType, fdItemId, documentId, updatedById } = params;
  const data = { partnerApprovedAt: null, selfApprovedAt: null, updatedById };
  const where = { id: fdItemId, documentId, deletedAt: null };

  switch (fdItemType) {
    case FDItemType.ACCOUNTS:
      await tx.accountAndAsset.updateMany({
        where: { ...where, assetAccountType: AssetAccountType.ACCOUNT },
        data,
      });
      return;
    case FDItemType.ASSETS:
      await tx.accountAndAsset.updateMany({
        where: { ...where, assetAccountType: AssetAccountType.ASSET },
        data,
      });
      return;
    case FDItemType.VEHICLES:
      await tx.vehicle.updateMany({ where, data });
      return;
    case FDItemType.INCOME:
      await tx.income.updateMany({ where, data });
      return;
    case FDItemType.REAL_ESTATE:
      await tx.realEstate.updateMany({ where, data });
      return;
    case FDItemType.BUSINESS:
      await tx.business.updateMany({ where, data });
      return;
    case FDItemType.DEBTS:
      await tx.debt.updateMany({ where, data });
      return;
    case FDItemType.INHERITANCE:
      await tx.inheritanceAndTrust.updateMany({
        where: { ...where, category: InheritanceTrust.INHERITANCE },
        data,
      });
      return;
    case FDItemType.TRUSTS:
      await tx.inheritanceAndTrust.updateMany({
        where: { ...where, category: InheritanceTrust.TRUST },
        data,
      });
      return;
    case FDItemType.CHILD_ACCOUNTS:
      // ChildAccount has `partnerApprovedAt`/`selfApprovedAt` columns and
      // the FD route factory mounts an `/approve` endpoint for it, even
      // though the current FD approval UI doesn't surface child accounts
      // in the document-wide approval flow (`FDApprovalCollections`
      // excludes them). An FD-answer edit on a child account must still
      // clear any per-item approval timestamps that may have been set via
      // the route, otherwise a partner-approved child account would
      // appear approved even after data changed.
      await tx.childAccount.updateMany({ where, data });
      return;
    case FDItemType.LEGACY_JOINT:
      // LegacyJointProperty has no `partnerApprovedAt`/`selfApprovedAt`
      // columns; per-item approval genuinely doesn't apply.
      return;
    default: {
      // Exhaustiveness check: if a new `FDItemType` is added without a
      // case above, this assignment fails to compile, naming the missing
      // variant. The `throw` is a runtime safety net for code paths that
      // bypass the type system.
      const _exhaustive: never = fdItemType;
      throw new Error(
        `clearFDApprovals: unhandled FDItemType ${String(_exhaustive)}`,
      );
    }
  }
}

// ---------------------------------------------------------------------------
// Shared fetch helpers
// ---------------------------------------------------------------------------

/**
 * Single source of truth for which FD response collections participate in
 * per-item approval, and (separately) which `FDItemType`s map to each
 * collection.
 *
 * `legacyJoint` is the only approval-bearing-shaped collection deliberately
 * absent: its rows have no `partnerApprovedAt` / `selfApprovedAt` columns,
 * so per-item approval doesn't apply at the data layer either.
 *
 * Whether a given collection is "joint-capable" (rows may legitimately
 * carry `isJoint: true`, in which case self-approval is also required) or
 * "effectively always-separate" on a given document type is *derived* from
 * `JOINT_OWNERSHIP_DOC_TYPES_BY_FD_ITEM_TYPE` via
 * `isCollectionJointCapableOnDocType` below. This avoids hardcoding
 * doc-type-dependent classifications (e.g. inheritance/trust is joint-
 * capable on POSTNUP but not PRENUP) in two places.
 */
const APPROVAL_COLLECTION_KEYS = [
  "accountsAndAssets",
  "businesses",
  "childAccounts",
  "debts",
  "incomes",
  "inheritancesAndTrusts",
  "realEstates",
  "vehicles",
] as const;
type ApprovalCollectionKey = (typeof APPROVAL_COLLECTION_KEYS)[number];

/**
 * Maps each approval-bearing collection key to the FDItemType(s) it
 * stores rows for. Typed as `Record<ApprovalCollectionKey, …>` so adding
 * a new collection key (or renaming one) is a compile error here until
 * the FDItemType mapping is updated.
 *
 * `inheritancesAndTrusts` maps to two `FDItemType`s because the collection
 * is a single Prisma model (`InheritanceAndTrust`) discriminated by
 * category at the API layer.
 */
const APPROVAL_COLLECTION_FD_ITEM_TYPES: Record<
  ApprovalCollectionKey,
  readonly FDItemType[]
> = {
  accountsAndAssets: [FDItemType.ACCOUNTS, FDItemType.ASSETS],
  businesses: [FDItemType.BUSINESS],
  childAccounts: [FDItemType.CHILD_ACCOUNTS],
  debts: [FDItemType.DEBTS],
  incomes: [FDItemType.INCOME],
  inheritancesAndTrusts: [FDItemType.INHERITANCE, FDItemType.TRUSTS],
  realEstates: [FDItemType.REAL_ESTATE],
  vehicles: [FDItemType.VEHICLES],
};

/**
 * Whether the collection's rows can legitimately have `isJoint: true` on
 * this document type, per `JOINT_OWNERSHIP_DOC_TYPES_BY_FD_ITEM_TYPE`.
 *
 * Used by `flattenForApprovalCheck` to decide whether to honor an item's
 * stored `isJoint` (when true, self-approval is required) or force it to
 * `false` (skipping self-approval). E.g.:
 *  - `inheritancesAndTrusts` is joint-capable on POSTNUP but not PRENUP.
 *  - `incomes` is never joint-capable on any doc type.
 *  - `accountsAndAssets` etc. are joint-capable on both.
 */
function isCollectionJointCapableOnDocType(
  key: ApprovalCollectionKey,
  docType: DocumentTemplateType,
): boolean {
  return APPROVAL_COLLECTION_FD_ITEM_TYPES[key].some((itemType) =>
    isJointOwnershipAllowedForDocType(itemType, docType),
  );
}

/**
 * Structural per-collection shape for the approval predicate; satisfied
 * by both Prisma `FDCollections` and the web client's JSON-serialized
 * shape. Uniform `readonly ApprovalItem[]` across collections because the
 * underlying Prisma row shape doesn't change with doc type; the per-
 * doc-type joint-capability classification lives in
 * `flattenForApprovalCheck` instead.
 */
export type FDApprovalCollections = Readonly<
  Record<ApprovalCollectionKey, readonly ApprovalItem[]>
>;

/**
 * Flattens every approvable FD collection into a single `ApprovalItem[]`.
 *
 * For each collection, decides per `docType` whether to preserve the
 * stored `isJoint` (joint-capable on this doc type → self-approval
 * required if `isJoint: true`) or force it to `false` (not joint-capable
 * on this doc type → only partner-approval matters). Driven off
 * `isCollectionJointCapableOnDocType`, which delegates to
 * `JOINT_OWNERSHIP_DOC_TYPES_BY_FD_ITEM_TYPE`, so the two classifications
 * can't drift.
 */
function flattenForApprovalCheck(
  collections: FDApprovalCollections,
  docType: DocumentTemplateType,
): ApprovalItem[] {
  const out: ApprovalItem[] = [];
  for (const key of APPROVAL_COLLECTION_KEYS) {
    const preserveIsJoint = isCollectionJointCapableOnDocType(key, docType);
    for (const item of collections[key]) {
      out.push(preserveIsJoint ? item : { ...item, isJoint: false });
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Public functions
// ---------------------------------------------------------------------------

/**
 * Per-user breakdown of the conditions the document-wide approval status
 * depends on. All flags are scoped to FD items *belonging to this user*.
 *
 * - `hasAllRequiredIncomeYears` / `hasAllRequiredFields`: client-side
 *   completeness mirror, enforced server-side.
 * - `hasAllPartnerApprovals`: `partnerApprovedAt` set+fresh on every owned
 *   item; set by the *other* user. Postnup-only (else `true`).
 * - `hasAllSelfApprovals`: `selfApprovedAt` set+fresh on every owned *joint*
 *   item; set by this user. Postnup-only (else `true`).
 * - `hasAllApprovals`: derived `partner && self` for legacy call sites.
 *
 * The split lets the client route actor-actionable gaps (own `selfApprovedAt`
 * / partner's `partnerApprovedAt`) to a Review CTA, vs partner-only gaps
 * (own `partnerApprovedAt` / partner's `selfApprovedAt`) to a Send Reminder.
 * Replaces a coarser boolean that popup-looped on partner-only gaps.
 */
export interface FDUserStatus {
  hasAllRequiredIncomeYears: boolean;
  hasAllRequiredFields: boolean;
  hasAllPartnerApprovals: boolean;
  hasAllSelfApprovals: boolean;
  hasAllApprovals: boolean;
}

export interface FDStatus {
  isComplete: boolean;
  users: Record<string, FDUserStatus>;
}

/**
 * Splits per-user approval completeness into the two axes that have
 * different actors. See the `FDUserStatus` doc above for the full
 * actor/partner-only quadrant table and the rationale for the split
 * (the previous coarse `hasAllApprovals` boolean led to a popup loop
 * where the customer was sent to `/financial-alignment` with nothing
 * actionable).
 */
export function computeUserApprovalsBreakdown(
  collections: FDApprovalCollections,
  userId: string,
  docType: DocumentTemplateType,
): { hasAllPartnerApprovals: boolean; hasAllSelfApprovals: boolean } {
  const ownItems = flattenForApprovalCheck(collections, docType).filter(
    (item) => item.userId === userId,
  );
  // `partnerApprovedAt` is required on every item the user owns.
  const hasAllPartnerApprovals = ownItems.every((item) =>
    isSideFreshlyApproved(item.partnerApprovedAt, item.updatedAt),
  );
  // `selfApprovedAt` is required only on joint items the user owns.
  const hasAllSelfApprovals = ownItems
    .filter((item) => item.isJoint === true)
    .every((item) =>
      isSideFreshlyApproved(item.selfApprovedAt, item.updatedAt),
    );
  return { hasAllPartnerApprovals, hasAllSelfApprovals };
}

function mergeCollections(parts: FDCollections[]): FDCollections {
  return {
    accountsAndAssets: parts.flatMap((p) => p.accountsAndAssets),
    businesses: parts.flatMap((p) => p.businesses),
    debts: parts.flatMap((p) => p.debts),
    incomes: parts.flatMap((p) => p.incomes),
    inheritancesAndTrusts: parts.flatMap((p) => p.inheritancesAndTrusts),
    legacyJoint: parts.flatMap((p) => p.legacyJoint),
    realEstates: parts.flatMap((p) => p.realEstates),
    vehicles: parts.flatMap((p) => p.vehicles),
    childAccounts: parts.flatMap((p) => p.childAccounts),
  };
}

/**
 * Per-user status flags + top-level `isComplete`. Returns `null` for missing /
 * soft-deleted docs or unreadable FD collections; unpaired docs return user A
 * only with `isComplete: false`. Finalized/reviewed docs and bubble imports
 * short-circuit to all-flags-true so already-signed contracts can't be
 * retro-flagged (pre-PNR FD edits clear those timestamps).
 */
export async function getFDStatus(
  prisma: ReadClient,
  documentId: string,
): Promise<FDStatus | null> {
  const document = await prisma.document.findUnique({
    where: { id: documentId, deletedAt: null },
    select: {
      userAId: true,
      userBId: true,
      finalizedAt: true,
      reviewedAt: true,
      lastGeneratedInputHash: true,
      template: { select: { type: true } },
    },
  });

  if (!document) return null;

  const userIds = document.userBId
    ? [document.userAId, document.userBId]
    : [document.userAId];
  const isPaired = !!document.userBId;

  const isTerminal =
    document.finalizedAt !== null ||
    document.reviewedAt !== null ||
    document.lastGeneratedInputHash === "bubble import";
  if (isTerminal) {
    return {
      isComplete: isPaired,
      users: Object.fromEntries(
        userIds.map((userId) => [
          userId,
          {
            hasAllRequiredIncomeYears: true,
            hasAllRequiredFields: true,
            hasAllPartnerApprovals: true,
            hasAllSelfApprovals: true,
            hasAllApprovals: true,
          },
        ]),
      ),
    };
  }

  const results = await Promise.all(
    userIds.map((userId) =>
      findAllFDCollections(prisma as PrismaClient, { userId, documentId }),
    ),
  );
  if (results.some((r) => !r.found)) return null;

  // Approval check needs the merged union: `partnerApprovedAt` on user A's
  // items is set by user B. `computeUserApprovalsBreakdown` filters by owner.
  const merged = mergeCollections(
    results.map((r) => (r.found ? r.collections : ({} as FDCollections))),
  );

  const isPostnup = document.template.type === DocumentTemplateType.POSTNUP;

  const users: Record<string, FDUserStatus> = {};
  userIds.forEach((userId, i) => {
    const result = results[i];
    if (!result?.found) return;
    const { hasAllPartnerApprovals, hasAllSelfApprovals } = isPostnup
      ? computeUserApprovalsBreakdown(merged, userId, document.template.type)
      : { hasAllPartnerApprovals: true, hasAllSelfApprovals: true };
    users[userId] = {
      hasAllRequiredIncomeYears: hasAllRequiredIncomeYears(
        result.collections.incomes,
      ),
      hasAllRequiredFields: allFDItemsHaveRequiredFields(result.collections),
      hasAllPartnerApprovals,
      hasAllSelfApprovals,
      hasAllApprovals: hasAllPartnerApprovals && hasAllSelfApprovals,
    };
  });

  // The document cannot be "complete" until both spouses are on it — the
  // partner's side is unevaluated when unpaired.
  const isComplete =
    isPaired &&
    Object.values(users).every(
      (s) =>
        s.hasAllRequiredIncomeYears &&
        s.hasAllRequiredFields &&
        s.hasAllApprovals,
    );

  return { isComplete, users };
}
